import React, { FC } from 'react';

import {
  Tab,
  Tabs,
  Flex,
  Text,
  Stack,
  Modal,
  Button,
  Heading,
  TabList,
  TabPanel,
  TabPanels,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  Spinner,
} from '@chakra-ui/react';

import { Pictures } from './components/Pictures';
import { EditForm } from './components/EditForm';
import { Invoicing } from './components/Invoicing';
import { GeneralInfo } from './components/GeneralInfo';
import { OrderHistory } from './components/OrderHistory';
import { CustomerRequest } from './components/CustomerRequest';
import { DeleteOrderButton } from './components/DeleteOrderButton';

import { EditOrderModalViewProps } from './EditOrderModal.props';
import {
  MdLocalActivity,
  MdLocalShipping,
  MdOutlineCases,
  MdUpload,
} from 'react-icons/md';
import { convertUTCToLocal } from 'utils/dateUtils';
import { BsUpload } from 'react-icons/bs';
import {
  Page,
  Document,
  PDFViewer,
  View,
  StyleSheet,
  Text as PDFText,
  Image,
} from '@react-pdf/renderer';
import OrderDetailsPdfTemplate from 'components/pdfs/OrderDetailPDFTemplate';
import OrderDetailsPDFGenerator from 'routes/portal/Orders/OrderDetailsPDFGenerator';
import styles from 'components/primitives/StatCards/styles';
import logo from 'assets/logos/r.World-company-logo.png';
import { useSKU } from 'hooks';
import StarBadge from 'components/pdfs/SVG/StarBadge';

const tabProps = {
  opacity: 0.5,
  fontWeight: '600',
  color: 'support.500',
  borderBottom: '2px solid',
  borderColor: 'primary.500',
};

const px = { base: '20px', md: '100px' };

const styless = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  table: {
    width: 'auto',
    marginVertical: 10,
    borderWidth: 1,
    borderColor: '#4CAF50',
  },
  tableRow: {
    flexDirection: 'row',
  },
  header: {
    backgroundColor: '#f6fffa',
    fontWeight: 'bold',
  },
  footer: {
    backgroundColor: '#d2f8d2',
    fontWeight: 'bold',
  },
  cell: {
    borderRightWidth: 1,
    borderRightColor: '#4CAF50',
    padding: 6,
    flexGrow: 1,
    textAlign: 'center',
  },
  lastCell: {
    borderRightWidth: 0,
  },
  boldText: {
    fontWeight: 'bold',
  },
  rightAlign: {
    textAlign: 'right',
  },
});

const EditOrderModalView: FC<EditOrderModalViewProps> = (props) => {
  const {
    order,
    isOpen,
    onClose,
    onSubmit,
    onDelete,
    isLoading,
    isEditing,
    isDeleting,
    setIsEditing,
    isRequest,
    setIsRequest,
    isCustomerRequest,
    renderNextOrderButton,
    renderPreviousOrderButton,
    isPendingInvoiceOrder,
  } = props;

  const totalCases = order?.details?.reduce(
    (acc, cur) => acc + cur.casesCount,
    0,
  );

  const { data: skuData, isLoading: isSkuLoading } = useSKU(
    'package',
    order?.washHub?._id,
    undefined,
    undefined,
    true,
  );

  return (
    <Modal
      size="5xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />

      <ModalContent borderRadius="10px">
        {!isEditing && order && renderNextOrderButton(order)}
        {!isEditing && order && renderPreviousOrderButton(order)}
        {!isEditing && (
          <ModalCloseButton
            _focus={{ outline: 'none' }}
            isDisabled={isLoading}
            top="40px"
            right="30px"
            size="20px"
          />
        )}

        {isSkuLoading && (
          <Flex justify="center" align="center" h="80vh">
            <Spinner />
          </Flex>
        )}

        {order && !isSkuLoading && (
          <>
            {isRequest ? (
              <ModalBody p="0">
                <CustomerRequest
                  order={order}
                  onClose={onClose}
                  onAccept={() => {
                    setIsRequest(false);
                    setIsEditing(true);
                  }}
                />
              </ModalBody>
            ) : isEditing ? (
              <ModalBody p="0">
                <EditForm
                  order={order}
                  onSubmit={onSubmit}
                  isLoading={isLoading}
                  onCancel={() => setIsEditing(false)}
                  isCustomerRequest={isCustomerRequest}
                />
              </ModalBody>
            ) : (
              <ModalBody p="0">
                <Flex
                  shadow="lg"
                  w="100%"
                  py="30px"
                  align="center"
                  justify="space-between"
                  px={px}>
                  <Heading fontSize="24px" fontWeight="600">
                    Order Details
                  </Heading>

                  <Flex gap="10px">
                    <OrderDetailsPDFGenerator
                      order={order}
                      skuData={skuData ?? []}
                    />

                    <DeleteOrderButton
                      onDelete={onDelete}
                      isDeleting={isDeleting}
                    />
                    <Button
                      isDisabled={isDeleting}
                      onClick={() => setIsEditing(true)}>
                      Edit Order
                    </Button>
                  </Flex>
                </Flex>
                <Flex
                  mt="30px"
                  w="100%"
                  gap="20px"
                  flexWrap="wrap"
                  justify="center">
                  <Flex gap="10px">
                    <Flex mt="2px">
                      <MdLocalActivity size="22px" color="#834EF3" />
                    </Flex>
                    <Stack spacing={0} fontSize="14px">
                      <Text fontWeight="600">Site</Text>
                      <Text fontWeight="600" opacity="0.6">
                        {order?.site?.name || ''}
                      </Text>
                    </Stack>
                  </Flex>

                  <Flex gap="10px" mx="80px">
                    <Flex mt="2px">
                      <MdLocalShipping size="22px" color="#912FBF" />
                    </Flex>
                    <Stack spacing={0} fontSize="14px">
                      <Text fontWeight="600">Delivery Date</Text>
                      <Text fontWeight="600" opacity="0.6">
                        {convertUTCToLocal(order?.deliveryAt, true, true)}
                      </Text>
                    </Stack>
                  </Flex>

                  <Flex gap="10px">
                    <Flex mt="2px">
                      <MdOutlineCases size="22px" color="primary.400" />
                    </Flex>
                    <Stack fontSize="14px" spacing={0}>
                      <Text fontWeight="600">Total Cases</Text>
                      <Text fontWeight="600" opacity="0.6">
                        {totalCases || '-'}
                      </Text>
                    </Stack>
                  </Flex>
                </Flex>

                <Tabs pb="20px" isLazy>
                  <TabList pt="20px" border="none" justifyContent="center">
                    <Tab {...tabProps}>General Info</Tab>
                    <Tab {...tabProps}>Activity History</Tab>
                    <Tab {...tabProps}>Pictures & Signatures</Tab>
                    <Tab {...tabProps}>Invoicing</Tab>
                    {/*{isPendingInvoiceOrder &&
                      ValidStatus.includes(order.status) && (
                        <Tab {...tabProps}>Invoicing</Tab>
                      )}*/}
                  </TabList>

                  <TabPanels px={px} minH="440px">
                    <TabPanel px="0">
                      <GeneralInfo order={order} />
                    </TabPanel>
                    <TabPanel px="0">
                      <OrderHistory orderId={order._id} />
                    </TabPanel>
                    <TabPanel px="0">
                      <Pictures orderId={order._id} />
                    </TabPanel>
                    <TabPanel px="0">
                      <Invoicing
                        order={order}
                        isInvoiced={order?.invoiced}
                        onClose={onClose}
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </ModalBody>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default EditOrderModalView;
